import { BuiltInAttribute } from "@orsane/schema/tasks";
import { useMemo } from "react";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { preparePreferenceTaskListAttributes } from "@/lib/utils";
import { useCustomAttributes } from "@/queries/custom-attribute-queries";
import type { Attribute } from "@/types/attribute";
import { useColumnBuilders } from "./ColumnBuilderContext";
import type { ColumnDef } from "./ColumnModel";
import { buildCreatedAtColumn } from "./column-builders/buildCreatedAtColumn";
import { buildCreatedByColumn } from "./column-builders/buildCreatedByColumn";
import { buildTaskNumberColumn } from "./column-builders/buildTaskNumberColumn";
import { buildTitleColumn } from "./column-builders/buildTitleColumn";

// Cache static columns that never change
const TITLE_COLUMN = buildTitleColumn();

export function useColumnModel(
    visibleAttributes: Attribute[],
    groupColumnId: string | null,
    sortColumnId: string | null
): ColumnDef[] {
    // Get stable column builders from context - these are memoized and won't change on every render
    const columnBuilders = useColumnBuilders();
    const { weekStartsOn } = useDateFormatter();

    // `visibleAttributes` comes from the stored task-list preference, which cached
    // { id, name, type } when the column was added. That snapshot goes stale the moment
    // an attribute type is renamed, and a stale type silently falls through to the
    // read-only column below. Resolve the live type by id so the cached one is only ever
    // a fallback for attributes we haven't loaded yet.
    const { data: liveAttributes } = useCustomAttributes();
    const liveTypeById = useMemo(() => {
        const map = new Map<number, string>();
        for (const attribute of liveAttributes ?? []) map.set(attribute.id, attribute.type);
        return map;
    }, [liveAttributes]);

    // Extract build functions from stable builders
    const buildPriorityColumn = columnBuilders.priority.build;
    const buildStatusColumn = columnBuilders.status.build;
    const buildDueDateColumn = columnBuilders.dueDate.build;
    const buildAssigneeColumn = columnBuilders.assignee.build;
    const buildListsColumn = columnBuilders.lists.build;
    const buildTimerColumn = columnBuilders.timer.build;
    const buildNumberColumn = columnBuilders.number.build;
    const buildTextColumn = columnBuilders.text.build;
    const buildSelectOneColumn = columnBuilders.selectOne.build;
    const buildOrbitSupportProjectColumn = columnBuilders.orbitSupportProject.build;
    const buildDefaultReadOnlyColumn = columnBuilders.defaultReadOnly.build;
    const buildParentTaskColumn = columnBuilders.parentTask.build;

    // Now we can include all builders in dependencies since they're stable!
    return useMemo(() => {
        const visibleAttributeIds = preparePreferenceTaskListAttributes(visibleAttributes).map((attr) => attr.id);
        const columns: ColumnDef[] = [];

        // Columns need to be included in the list if they are visible or if they are the group column
        // But title is always visible so it is always included

        columns.push(TITLE_COLUMN);

        // ID

        if (
            visibleAttributeIds.includes(BuiltInAttribute.id) ||
            groupColumnId === BuiltInAttribute.id ||
            sortColumnId === BuiltInAttribute.id
        ) {
            columns.push(buildTaskNumberColumn(visibleAttributeIds.includes(BuiltInAttribute.id)));
        }

        // Priority

        if (
            visibleAttributeIds.includes(BuiltInAttribute.priority) ||
            groupColumnId === BuiltInAttribute.priority ||
            sortColumnId === BuiltInAttribute.priority
        ) {
            columns.push(buildPriorityColumn(visibleAttributeIds.includes(BuiltInAttribute.priority)));
        }

        // Status

        if (
            visibleAttributeIds.includes(BuiltInAttribute.taskStatus) ||
            groupColumnId === BuiltInAttribute.taskStatus ||
            sortColumnId === BuiltInAttribute.taskStatus
        ) {
            columns.push(buildStatusColumn(visibleAttributeIds.includes(BuiltInAttribute.taskStatus)));
        }

        // Due date

        if (
            visibleAttributeIds.includes(BuiltInAttribute.dueDate) ||
            groupColumnId === BuiltInAttribute.dueDate ||
            sortColumnId === BuiltInAttribute.dueDate
        ) {
            columns.push(buildDueDateColumn(visibleAttributeIds.includes(BuiltInAttribute.dueDate)));
        }

        // Created at
        if (
            visibleAttributeIds.includes(BuiltInAttribute.createdAt) ||
            groupColumnId === BuiltInAttribute.createdAt ||
            sortColumnId === BuiltInAttribute.createdAt
        ) {
            columns.push(buildCreatedAtColumn(visibleAttributeIds.includes(BuiltInAttribute.createdAt), weekStartsOn));
        }

        // Assignee
        if (
            visibleAttributeIds.includes(BuiltInAttribute.assignee) ||
            groupColumnId === BuiltInAttribute.assignee ||
            sortColumnId === BuiltInAttribute.assignee
        ) {
            columns.push(buildAssigneeColumn(visibleAttributeIds.includes(BuiltInAttribute.assignee)));
        }

        // Created by
        if (
            visibleAttributeIds.includes(BuiltInAttribute.createdBy) ||
            groupColumnId === BuiltInAttribute.createdBy ||
            sortColumnId === BuiltInAttribute.createdBy
        ) {
            columns.push(buildCreatedByColumn(visibleAttributeIds.includes(BuiltInAttribute.createdBy)));
        }

        // Lists
        if (
            visibleAttributeIds.includes(BuiltInAttribute.lists) ||
            groupColumnId === BuiltInAttribute.lists ||
            sortColumnId === BuiltInAttribute.lists
        ) {
            columns.push(buildListsColumn(visibleAttributeIds.includes(BuiltInAttribute.lists)));
        }

        // Parent task
        if (
            visibleAttributeIds.includes(BuiltInAttribute.parentTask) ||
            groupColumnId === BuiltInAttribute.parentTask ||
            sortColumnId === BuiltInAttribute.parentTask
        ) {
            columns.push(buildParentTaskColumn(visibleAttributeIds.includes(BuiltInAttribute.parentTask)));
        }

        // Manual sort (stub column — sorting is handled by manualOrder array, not getSortValue)
        if (sortColumnId === BuiltInAttribute.manual) {
            columns.push({
                id: BuiltInAttribute.manual,
                header: "Manual",
                isVisible: false,
                cssColDef: "0px",
                isRight: false,
                isReadOnly: true,
                getSortValue: () => 0,
                getGroupHeader: () => null,
                cell: () => null,
            });
        }

        // Custom Attributes
        const customAttributes = preparePreferenceTaskListAttributes(visibleAttributes).filter(
            (x) => x.type !== "builtin"
        );
        for (const cachedAttribute of customAttributes) {
            const attribute =
                typeof cachedAttribute.id === "number" && liveTypeById.has(cachedAttribute.id)
                    ? { ...cachedAttribute, type: liveTypeById.get(cachedAttribute.id)! }
                    : cachedAttribute;

            if (attribute.type === "timer") {
                const col = buildTimerColumn(visibleAttributeIds.includes(attribute.id), attribute);
                columns.push(col);
            } else if (attribute.type === "number") {
                const col = buildNumberColumn(visibleAttributeIds.includes(attribute.id), attribute);
                columns.push(col);
            } else if (attribute.type === "text") {
                const col = buildTextColumn(visibleAttributeIds.includes(attribute.id), attribute);
                columns.push(col);
            } else if (attribute.type === "select_one") {
                const col = buildSelectOneColumn(visibleAttributeIds.includes(attribute.id), attribute);
                columns.push(col);
            } else if (attribute.type === "orbit_support_project") {
                const col = buildOrbitSupportProjectColumn(visibleAttributeIds.includes(attribute.id), attribute);
                columns.push(col);
            } else {
                const col = buildDefaultReadOnlyColumn(visibleAttributeIds.includes(attribute.id), attribute);
                columns.push(col);
            }
        }

        return columns;
    }, [
        visibleAttributes,
        groupColumnId,
        buildPriorityColumn,
        buildStatusColumn,
        buildDueDateColumn,
        buildAssigneeColumn,
        buildListsColumn,
        buildTimerColumn,
        buildNumberColumn,
        buildTextColumn,
        buildOrbitSupportProjectColumn,
        buildDefaultReadOnlyColumn,
        liveTypeById,
        buildSelectOneColumn,
        sortColumnId,
        weekStartsOn,
    ]);
}
